import fetch from '@rd-web-markets/shared/dist/services/Fetcher';
import { request } from '@rd-web-markets/shared/dist/services/request';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { useErrorHandling } from '../hooks/useErrorHandling';
import { setIsUnderTechnicalReview } from '../store/features/reviewSlice';
import serviceMethods from './service_methods';
import { buildService } from './service';
import engagementTeamService from './claim_groups/engagement_team.service';
const api = buildService('/companies', {
  only: ['get']
});
let CompanyService = {};
CompanyService = {
  useFetch(_ref) {
    let {
      companyId,
      setLoading
    } = _ref;
    const [company, setCompany, fetchCompany] = serviceMethods.useFetch({
      callback: useCallback(async () => await api.get(companyId), [companyId]),
      setLoading,
      initialStateValue: null
    });
    return [company, setCompany, fetchCompany];
  },
  useRefresh(companyId, setCompanies, setLoading) {
    const refreshCompany = useErrorHandling(useCallback(async () => {
      setLoading(true);
      await this.refresh(companyId);
      const response = await this.all();
      setCompanies(response.companies);
    }, [companyId, setCompanies, setLoading]), () => setLoading(false));
    return refreshCompany;
  },
  useDelete(companyId, setCompanies, setLoading, confirmationButtonText, refreshCompanies) {
    const deleteCompany = serviceMethods.useDelete({
      callback: useCallback(async () => {
        await this.delete(companyId);
        const response = await this.all();
        setCompanies(response.companies);
        refreshCompanies();
      }, [companyId, refreshCompanies, setCompanies]),
      setLoading,
      modalConfirm: {
        type: 'delete',
        confirmationButtonText
      }
    });
    return deleteCompany;
  },
  useUpdate(setCompany, setLoading) {
    let callback = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : () => {};
    const updateCompany = useErrorHandling(useCallback(async company => {
      setLoading && setLoading(true);
      const updatedCompany = await this.update(company);
      setCompany({
        ...updatedCompany
      });
      await callback(updatedCompany);
    }, [setCompany, setLoading, callback]), useCallback(() => {
      setLoading && setLoading(false);
    }, [setLoading]));
    return updateCompany;
  },
  useGetCompanyAndClaimGroup: function (companyId, claimGroupId) {
    let initialCompanyState = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    let initialClaimGroupState = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
    let setLoading = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : () => {};
    const [company, setCompany] = useState(initialCompanyState);
    const [claimGroup, setClaimGroup] = useState(initialClaimGroupState);
    const dispatch = useDispatch();
    const setClaimGroupFromCompany = useCallback(async company => {
      const selectedClaimGroup = company.claim_groups.find(c => c.id === Number(claimGroupId)) || company.claim_groups[0];
      const engagementTeam = await engagementTeamService.get(selectedClaimGroup.id);
      const userAccesses = [...engagementTeam.clients, ...engagementTeam.accountants].map(_ref2 => {
        let {
          user_access: userAccess,
          ...user
        } = _ref2;
        return {
          ...userAccess,
          user
        };
      });
      const freshClaimGroup = {
        ...selectedClaimGroup,
        user_accesses: userAccesses,
        company: {
          ...company
        }
      };
      setClaimGroup(freshClaimGroup);
      dispatch(setIsUnderTechnicalReview(!!selectedClaimGroup.active_change_set));
    }, [claimGroupId, dispatch, setClaimGroup]);
    const updateCompany = CompanyService.useUpdate(setCompany, setLoading, useCallback(updatedCompany => {
      return setClaimGroupFromCompany(updatedCompany);
    }, [setClaimGroupFromCompany]));
    const fetchCompanyAndClaimGroup = useErrorHandling(useCallback(async () => {
      if (companyId && claimGroupId) {
        const fetchedCompany = await CompanyService.get(companyId);
        await setClaimGroupFromCompany(fetchedCompany);
        setCompany(fetchedCompany);
      }
    }, [companyId, claimGroupId, setClaimGroupFromCompany]));
    useEffect(() => {
      fetchCompanyAndClaimGroup();
    }, [fetchCompanyAndClaimGroup]);
    return [company, setCompany, claimGroup, setClaimGroup, fetchCompanyAndClaimGroup, updateCompany];
  },
  all: function () {
    let queryParams = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    return fetch(`/api/companies?${queryParams}`, request.get);
  },
  get: company_id => {
    return fetch(`/api/companies/${company_id}`, request.get);
  },
  update: company => {
    return fetch(`/api/companies/${company.id}`, {
      ...request.put,
      body: JSON.stringify({
        company
      })
    });
  },
  delete: company_id => {
    return fetch(`/api/companies/${company_id}`, request.delete);
  }
};
export { CompanyService };